import React, { useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { X, Building2, Loader2, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';

// Validation schema
const companySchema = z.object({
  name: z.string().min(2, 'Company name must be at least 2 characters'),
  cost_center: z.string().min(1, 'Cost center is required'),
  workday_supervisory_org: z.string().min(1, 'Workday supervisory organization is required'),
  industry: z.string().optional(),
  website: z.string().url('Must be a valid URL').optional().or(z.literal('')),
  notes: z.string().optional()
});

const AddCompanyModal = ({ isOpen, onClose, onSuccess }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [similarCompanies, setSimilarCompanies] = useState([]);
  const [showDuplicateWarning, setShowDuplicateWarning] = useState(false);
  const [pendingData, setPendingData] = useState(null);
  const [companyName, setCompanyName] = useState('');
  const [duplicateWarning, setDuplicateWarning] = useState(null);
  const [allCompanies, setAllCompanies] = useState([]);

  const {
    control,
    handleSubmit,
    formState: { errors, isValid },
    reset,
    watch
  } = useForm({
    resolver: zodResolver(companySchema),
    defaultValues: {
      name: '',
      cost_center: '',
      workday_supervisory_org: '',
      industry: '',
      website: '',
      notes: ''
    },
    mode: 'onChange'
  });
  
  // Watch the name field for real-time duplicate checking
  const watchedName = watch('name');

  // Fetch all companies when modal opens
  React.useEffect(() => {
    if (isOpen) {
      fetchAllCompanies();
    }
  }, [isOpen]);
  
  // Real-time duplicate checking
  React.useEffect(() => {
    if (watchedName && watchedName.length >= 3) {
      const timer = setTimeout(() => {
        checkForDuplicates(watchedName);
      }, 500);
      return () => clearTimeout(timer);
    } else {
      setDuplicateWarning(null);
    }
  }, [watchedName, allCompanies]);
  
  const fetchAllCompanies = async () => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${backendUrl}/api/customers`);
      if (response.ok) {
        const data = await response.json();
        setAllCompanies(data.customers || []);
      }
    } catch (error) {
      console.error('Error fetching companies:', error);
    }
  };
  
  const checkForDuplicates = (name) => {
    const nameLower = name.toLowerCase().trim();
    
    // Check for exact match
    const exactMatch = allCompanies.find(c => 
      (c.client_name || c.company_name || '').toLowerCase().trim() === nameLower
    );
    
    if (exactMatch) {
      setDuplicateWarning({
        type: 'error',
        message: `Company "${name}" already exists. Please select from dropdown.`,
        company: exactMatch,
        canProceed: false
      });
      return;
    }
    
    // Check for similar matches (fuzzy)
    const similar = allCompanies.filter(c => {
      const companyName = (c.client_name || c.company_name || '').toLowerCase().trim();
      const searchName = nameLower;
      
      // Various similarity checks
      if (companyName.includes(searchName) || searchName.includes(companyName)) {
        return true;
      }
      
      // Calculate similarity score
      const similarity = calculateSimilarity(companyName, searchName);
      return similarity > 0.7;
    });
    
    if (similar.length > 0) {
      setDuplicateWarning({
        type: 'warning',
        message: `Similar companies found. Please verify this is a new company.`,
        companies: similar,
        canProceed: true
      });
    } else {
      setDuplicateWarning(null);
    }
  };
  
  // Calculate string similarity (Levenshtein-based)
  const calculateSimilarity = (str1, str2) => {
    const longer = str1.length > str2.length ? str1 : str2;
    const shorter = str1.length > str2.length ? str2 : str1;
    if (longer.length === 0) return 1.0;
    
    const editDistance = levenshteinDistance(longer, shorter);
    return (longer.length - editDistance) / longer.length;
  };
  
  // Levenshtein distance algorithm
  const levenshteinDistance = (str1, str2) => {
    const matrix = [];
    
    for (let i = 0; i <= str2.length; i++) {
      matrix[i] = [i];
    }
    
    for (let j = 0; j <= str1.length; j++) {
      matrix[0][j] = j;
    }
    
    for (let i = 1; i <= str2.length; i++) {
      for (let j = 1; j <= str1.length; j++) {
        if (str2.charAt(i - 1) === str1.charAt(j - 1)) {
          matrix[i][j] = matrix[i - 1][j - 1];
        } else {
          matrix[i][j] = Math.min(
            matrix[i - 1][j - 1] + 1,
            matrix[i][j - 1] + 1,
            matrix[i - 1][j] + 1
          );
        }
      }
    }
    
    return matrix[str2.length][str1.length];
  };

  const onSubmit = async (data, forceCreate = false) => {
    // Block submission if exact match found
    if (duplicateWarning?.type === 'error') {
      toast.error('Cannot create duplicate company. Please use existing company from dropdown.');
      return;
    }
    setIsSubmitting(true);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      
      const url = forceCreate 
        ? `${backendUrl}/api/client-companies?force_create=true`
        : `${backendUrl}/api/client-companies`;
      
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(data)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to create company');
      }

      const result = await response.json();
      
      // Check if backend returned warnings about similar companies
      if (!forceCreate && result.company?.warnings?.has_similar && result.company.warnings.similar_companies.length > 0) {
        // Show duplicate warning dialog
        setSimilarCompanies(result.company.warnings.similar_companies);
        setPendingData(data);
        setShowDuplicateWarning(true);
        setIsSubmitting(false);
        return;
      }
      
      toast.success('Company added successfully!');
      reset();
      setSimilarCompanies([]);
      setShowDuplicateWarning(false);
      setPendingData(null);
      onSuccess && onSuccess(result.company);
      onClose();
    } catch (error) {
      console.error('Error creating company:', error);
      toast.error(error.message || 'Failed to create company');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleForceCreate = () => {
    setShowDuplicateWarning(false);
    onSubmit(pendingData, true);
  };

  const handleSelectExisting = (company) => {
    setShowDuplicateWarning(false);
    setSimilarCompanies([]);
    setPendingData(null);
    reset();
    toast.success('Selected existing company');
    onSuccess && onSuccess(company);
    onClose();
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl" data-testid="add-company-modal">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary/10 rounded-lg">
              <Building2 className="h-5 w-5 text-primary" />
            </div>
            <div>
              <DialogTitle className="text-xl font-semibold">Add New Company</DialogTitle>
              <DialogDescription className="text-sm text-muted-foreground mt-1">
                Enter company details for Workday integration and finance tracking
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 mt-4">
          {/* Company Name */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Company Name <span className="text-destructive">*</span>
            </label>
            <Controller
              name="name"
              control={control}
              render={({ field }) => (
                <div>
                  <Input
                    {...field}
                    placeholder="e.g., BECU, 7-Eleven, Centerpoint Energy"
                    className={errors.name ? 'border-destructive' : ''}
                    data-testid="company-name-input"
                    aria-required="true"
                    autoFocus
                  />
                  {errors.name && (
                    <p className="text-xs text-destructive mt-1">{errors.name.message}</p>
                  )}
                  
                  {/* Duplicate Warning */}
                  {duplicateWarning && (
                    <div className={`mt-3 p-3 rounded-lg border ${
                      duplicateWarning.type === 'error' 
                        ? 'bg-red-50 border-red-200' 
                        : 'bg-yellow-50 border-yellow-200'
                    }`}>
                      <div className="flex items-start gap-2">
                        <AlertCircle className={`h-5 w-5 mt-0.5 flex-shrink-0 ${
                          duplicateWarning.type === 'error' ? 'text-red-600' : 'text-yellow-600'
                        }`} />
                        <div className="flex-1">
                          <p className={`text-sm font-medium ${
                            duplicateWarning.type === 'error' ? 'text-red-800' : 'text-yellow-800'
                          }`}>
                            {duplicateWarning.message}
                          </p>
                          
                          {/* Show exact match */}
                          {duplicateWarning.company && (
                            <div className="mt-2 text-sm text-gray-700 bg-white p-2 rounded border border-gray-200">
                              <p><strong>{duplicateWarning.company.client_name || duplicateWarning.company.company_name}</strong></p>
                              {duplicateWarning.company.account_manager && (
                                <p className="text-xs text-gray-600 mt-1">
                                  Account Manager: {duplicateWarning.company.account_manager}
                                </p>
                              )}
                            </div>
                          )}
                          
                          {/* Show similar companies */}
                          {duplicateWarning.companies && duplicateWarning.companies.length > 0 && (
                            <ul className="mt-2 space-y-1">
                              {duplicateWarning.companies.slice(0, 3).map((company, idx) => (
                                <li key={idx} className="text-sm text-gray-700 bg-white p-2 rounded border border-gray-200">
                                  <strong>{company.client_name || company.company_name}</strong>
                                  {company.account_manager && (
                                    <span className="text-xs text-gray-600 ml-2">
                                      (Managed by {company.account_manager})
                                    </span>
                                  )}
                                </li>
                              ))}
                              {duplicateWarning.companies.length > 3 && (
                                <li className="text-xs text-gray-600 ml-2">
                                  ...and {duplicateWarning.companies.length - 3} more
                                </li>
                              )}
                            </ul>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Cost Center */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Cost Center <span className="text-destructive">*</span>
              </label>
              <Controller
                name="cost_center"
                control={control}
                render={({ field }) => (
                  <div>
                    <Input
                      {...field}
                      placeholder="e.g., CC-BECU-001"
                      className={errors.cost_center ? 'border-destructive' : ''}
                      data-testid="cost-center-input"
                      aria-required="true"
                    />
                    {errors.cost_center && (
                      <p className="text-xs text-destructive mt-1">{errors.cost_center.message}</p>
                    )}
                    <p className="text-xs text-muted-foreground mt-1">
                      From Workday, for finance reporting
                    </p>
                  </div>
                )}
              />
            </div>

            {/* Workday Supervisory Org */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Workday Supervisory Org <span className="text-destructive">*</span>
              </label>
              <Controller
                name="workday_supervisory_org"
                control={control}
                render={({ field }) => (
                  <div>
                    <Input
                      {...field}
                      placeholder="e.g., WSO-BECU-ENG"
                      className={errors.workday_supervisory_org ? 'border-destructive' : ''}
                      data-testid="workday-org-input"
                      aria-required="true"
                    />
                    {errors.workday_supervisory_org && (
                      <p className="text-xs text-destructive mt-1">{errors.workday_supervisory_org.message}</p>
                    )}
                    <p className="text-xs text-muted-foreground mt-1">
                      For Workday sync and billing
                    </p>
                  </div>
                )}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Industry */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Industry
              </label>
              <Controller
                name="industry"
                control={control}
                render={({ field }) => (
                  <Input
                    {...field}
                    placeholder="e.g., Financial Services, Retail"
                    data-testid="industry-input"
                  />
                )}
              />
            </div>

            {/* Website */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Website
              </label>
              <Controller
                name="website"
                control={control}
                render={({ field }) => (
                  <div>
                    <Input
                      {...field}
                      placeholder="https://www.company.com"
                      className={errors.website ? 'border-destructive' : ''}
                      data-testid="website-input"
                    />
                    {errors.website && (
                      <p className="text-xs text-destructive mt-1">{errors.website.message}</p>
                    )}
                  </div>
                )}
              />
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Notes
            </label>
            <Controller
              name="notes"
              control={control}
              render={({ field }) => (
                <Textarea
                  {...field}
                  placeholder="Any additional information about this company..."
                  rows={3}
                  data-testid="notes-textarea"
                />
              )}
            />
          </div>

          {/* Duplicate Warning */}
          {showDuplicateWarning && similarCompanies.length > 0 && (
            <div className="bg-yellow-50 border-2 border-yellow-400 rounded-lg p-4 space-y-3" data-testid="duplicate-warning-panel">
              <div className="flex items-start gap-3">
                <div className="p-2 bg-yellow-100 rounded-lg flex-shrink-0">
                  <AlertCircle className="h-5 w-5 text-yellow-600" />
                </div>
                <div className="flex-1">
                  <h4 className="font-semibold text-yellow-900 mb-1">Similar Companies Found</h4>
                  <p className="text-sm text-yellow-800 mb-3">
                    We found {similarCompanies.length} similar {similarCompanies.length === 1 ? 'company' : 'companies'} in the system. 
                    Did you mean one of these?
                  </p>
                  
                  <div className="space-y-2 mb-3">
                    {similarCompanies.map((company) => (
                      <div 
                        key={company.id} 
                        className="flex items-center justify-between p-3 bg-white border border-yellow-300 rounded-lg hover:border-yellow-500 transition-colors"
                        data-testid={`similar-company-${company.id}`}
                      >
                        <div className="flex-1">
                          <p className="font-medium text-gray-900">{company.name}</p>
                          <div className="flex gap-4 mt-1">
                            {company.industry && (
                              <span className="text-xs text-gray-600">Industry: {company.industry}</span>
                            )}
                            {company.website && (
                              <span className="text-xs text-gray-600 truncate max-w-[200px]">{company.website}</span>
                            )}
                          </div>
                        </div>
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          onClick={() => handleSelectExisting(company)}
                          data-testid={`select-existing-${company.id}`}
                          className="ml-3"
                        >
                          Use This
                        </Button>
                      </div>
                    ))}
                  </div>
                  
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        setShowDuplicateWarning(false);
                        setSimilarCompanies([]);
                        setPendingData(null);
                      }}
                      data-testid="cancel-duplicate-button"
                      className="flex-1"
                    >
                      Cancel
                    </Button>
                    <Button
                      type="button"
                      onClick={handleForceCreate}
                      disabled={isSubmitting}
                      data-testid="create-anyway-button"
                      className="flex-1 bg-yellow-600 text-white hover:bg-yellow-700"
                    >
                      {isSubmitting ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Creating...
                        </>
                      ) : (
                        'Create New Anyway'
                      )}
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Footer Actions */}
          {!showDuplicateWarning && (
            <div className="flex items-center justify-end gap-3 pt-4 border-t">
              <Button
                type="button"
                variant="outline"
                onClick={handleClose}
                disabled={isSubmitting}
                data-testid="cancel-button"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={!isValid || isSubmitting || duplicateWarning?.type === 'error'}
                data-testid="add-company-button"
                className={`bg-primary text-primary-foreground hover:bg-primary/90 ${
                  duplicateWarning?.type === 'error' ? 'opacity-50 cursor-not-allowed' : ''
                }`}
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Adding...
                  </>
                ) : (
                  'Add Company'
                )}
              </Button>
            </div>
          )}
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AddCompanyModal;
